/**
 * Icon — the OnFlow glyph set. Ported from the "OnFlow Design System" project
 * (components/navigation/Icon.jsx).
 *
 * One spec for every glyph: 24x24 viewBox, fill none, stroke currentColor,
 * round caps. Size and stroke width are the only knobs, so an icon inherits
 * colour from whatever it sits in and never carries its own palette.
 */
import type { CSSProperties, ReactElement } from 'react';

const PATHS: Record<string, ReactElement> = {
  home: (
    <>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </>
  ),
  diet: (
    <>
      <path d="M12 20.9c1.4 0 2.6 1 3.8 1 2.8 0 5.7-7.6 5.7-11.6a4.7 4.7 0 0 0-4.8-4.5c-2.1 0-3.8 1.4-4.7 1.9-1-.5-2.6-1.9-4.7-1.9a4.7 4.7 0 0 0-4.8 4.5c0 4 2.9 11.6 5.7 11.6 1.2 0 2.4-1 3.8-1Z" />
      <path d="M12 6.7c.1-1.6.6-3.1 2-4.7" />
    </>
  ),
  dumbbell: (
    <>
      <rect x="5.5" y="7" width="3" height="10" rx="1.2" />
      <rect x="15.5" y="7" width="3" height="10" rx="1.2" />
      <path d="M8.5 12h7" />
      <path d="M3 9.5v5" />
      <path d="M21 9.5v5" />
    </>
  ),
  chart: (
    <>
      <line x1="4" y1="20" x2="4" y2="10" />
      <line x1="10" y1="20" x2="10" y2="4" />
      <line x1="16" y1="20" x2="16" y2="14" />
      <line x1="22" y1="20" x2="2" y2="20" />
    </>
  ),
  user: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  arrowRight: <path d="M4 12h14M13 6l6 6-6 6" />,
  chevronDown: <polyline points="6 9 12 15 18 9" />,
  close: (
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>
  ),
  check: <polyline points="20 6 9 17 4 12" />,
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </>
  ),
  plus: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  trash: (
    <>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </>
  ),
};

export type IconName = keyof typeof PATHS;

interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  style?: CSSProperties;
  className?: string;
}

export function Icon({ name, size = 24, strokeWidth = 2, style, className }: IconProps) {
  const d = PATHS[name];
  if (!d) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      className={className}
      aria-hidden
    >
      {d}
    </svg>
  );
}
